import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {Navigate} from 'react-router-dom';
import {getUserApplications} from '../../fetch/applicationApi';
import {RootState} from '../../redux/rootReducer';
import {ApplicationType} from '../../types/ApplicationType';
import './ApplicationLauncher.scss';

const ApplicationLauncher = () => {
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const [applications, setApplications] = useState<ApplicationType[]>([]);
    const [loading, setLoading] = useState(true);
    const userData = useSelector((state: RootState) => state.login.userData);

    useEffect(() => {
        getUserApplications(dispatch).then(response => {
            if (response.success && response.result) {
                setApplications(response.result);
            }
        }).finally(() => setLoading(false));
    }, [dispatch]);

    const launchApplication = (app: ApplicationType) => {
        window.location.href = app.url;
    };

    if (!userData) {
        return <Navigate to="/login" replace/>;
    }

    return (
        <div className="application-launcher my-4">
            <div className="launcher-header">
                <h5 className="mb-2">{t('applications.TITLE')}</h5>
                <p className="text-muted">{t('applications.DESCRIPTION')}</p>
            </div>

            {loading ? (
                <div className="loading-state">
                    <div className="spinner"></div>
                </div>
            ) : applications.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="7" height="7" rx="1"/>
                            <rect x="14" y="3" width="7" height="7" rx="1"/>
                            <rect x="3" y="14" width="7" height="7" rx="1"/>
                            <rect x="14" y="14" width="7" height="7" rx="1"/>
                        </svg>
                    </div>
                    <p>{t('applications.NO_APPLICATIONS')}</p>
                </div>
            ) : (
                <div className="app-list">
                    {applications.map((app) => (
                        <div
                            key={app.clientId}
                            className="app-card"
                            onClick={() => launchApplication(app)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && launchApplication(app)}
                        >
                            <div className="app-logo">
                                {app.logoUrl ? (
                                    <img src={app.logoUrl} alt={app.displayName || app.clientName}/>
                                ) : (
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="3" y="3" width="7" height="7" rx="1"/>
                                        <rect x="14" y="3" width="7" height="7" rx="1"/>
                                        <rect x="3" y="14" width="7" height="7" rx="1"/>
                                        <rect x="14" y="14" width="7" height="7" rx="1"/>
                                    </svg>
                                )}
                            </div>
                            <div className="app-info">
                                <span className="app-name">{app.displayName || app.clientName}</span>
                                <span className="app-description">{t(`applications.app.${app.clientId}`, {defaultValue: ''})}</span>
                            </div>
                            <svg className="app-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                 strokeLinejoin="round">
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ApplicationLauncher;
